import { nanoid } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import * as subscriptions from 'src/graphql/subscriptions'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import { startPreparePurchaseOcr } from 'src/redux/slices/purchases'
import type {
	GetPreparePurchaseQueryApi,
	ReduxExportInfo,
	ReduxPreparePurchase,
	ReduxQuote,
	ReduxRecapInfo,
	ReduxS3Object,
} from 'src/types/apiType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getFilterArray } from 'src/utils/fetchUtils'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { getProperUpdateValues } from 'src/utils/updateApiUtil'

export const preparePurchaseApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchPreparePurchase: builder.query({
			query: ({
				tenantId,
				pageName,
				startDate,
				endDate,
			}: {
				tenantId: string
				pageName?: string
				startDate?: any
				endDate?: any
			}) => {
				let currFilter: any = {
					activeFlag: { eq: true },
					and: [
						{
							createdOn: {
								gt: startDate,
							},
						},
						{ createdOn: { lt: endDate } },
					],
				}
				if (!startDate || !endDate) {
					currFilter = {
						activeFlag: { eq: true },
					}
				}
				if (pageName) {
					currFilter = {
						...currFilter,
						currentPage: { eq: pageName },
					}
				}
				return {
					queryName: 'preparePurchaseByTenantId',
					data: {
						tenantId,
						filter: currFilter,
						limit: 1000,
					},
					isList: true,
				}
			},
			async onCacheEntryAdded(
				arg,
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }
			) {
				const subscription = API.graphql(
					graphqlOperation(
						subscriptions.onUpdatePreparePurchaseByTenantId,
						{
							tenantId: arg.tenantId,
						}
					)
				)
				try {
					await cacheDataLoaded
					// @ts-expect-error Check if this is fixed in future https://github.com/aws-amplify/amplify-js/issues/7589
					subscription.subscribe({
						next: ({ provider, value }: any) => {
							const data = value?.data
								?.onUpdatePreparePurchaseByTenantId as ReduxPreparePurchase
							console.log('data: ', data)
							if (!data?.id) return
							updateCachedData((draft) => {
								draft.map((item) => {
									if (item.id === data.id) {
										console.log('item: ', item)
										for (const [
											key,
											value,
										] of Object.entries(data)) {
											if (
												typeof value === 'undefined' ||
												value === null ||
												(typeof value === 'string' &&
													value.trim() === '')
											) {
												continue
											}

											if (
												key === 'ocrStatus' &&
												value === 'Complete' &&
												item.ocrStatus !== 'Complete'
											) {
												makeSuccessToast(
													`Ocr processing for ${item.fileName} done`
												)
											}
											const typedKey =
												key as keyof typeof item
											const typedValue = value as any
											// @ts-expect-error
											item[typedKey] = typedValue
										}
									}
									return item
								})
							})
						},
						error: (error: any) => console.warn(error),
					})
				} catch (error) {
					console.error(error)
				}
				// cacheEntryRemoved will resolve when the cache subscription is no longer active
				await cacheEntryRemoved
				// perform cleanup steps once the `cacheEntryRemoved` promise resolves
				try {
					// @ts-expect-error
					subscription.unsubscribe()
				} catch (err: any) {
					console.warn('err on unsubscribe: ', err)
				}
			},
			transformResponse: (response: ReduxPreparePurchase[]) => response,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'PreparePurchase' as const,
								id: id as string,
							})),
							{ type: 'PreparePurchase', id: 'LIST' },
						]
					: [{ type: 'PreparePurchase', id: 'LIST' }],
		}),
		fetchPreparePurchaseFreelancer: builder.query({
			query: ({
				tenantId,
				pageName,
				projectArray,
				startDate,
				endDate,
			}: {
				tenantId: string
				pageName?: string
				projectArray: string[]
				startDate?: any
				endDate?: any
			}) => {
				let currFilter: any = {
					activeFlag: { eq: true },
					and: [
						{
							createdOn: {
								gt: startDate,
							},
						},
						{ createdOn: { lt: endDate } },
					],
				}
				if (!startDate || !endDate) {
					currFilter = {
						activeFlag: { eq: true },
					}
				}
				if (pageName) {
					currFilter = {
						...currFilter,
						currentPage: { eq: pageName },
					}
				}
				return {
					queryName: 'preparePurchaseByTenantId',
					data: {
						tenantId,
						filter: {
							activeFlag: { eq: true },
							currentPage: { eq: pageName },
							or: getFilterArray('projectId', projectArray),
						},
						limit: 1000,
					},
					isList: true,
				}
			},
			async onCacheEntryAdded(
				arg,
				{ updateCachedData, cacheDataLoaded, cacheEntryRemoved }
			) {
				const subscription = API.graphql(
					graphqlOperation(
						subscriptions.onUpdatePreparePurchaseByTenantId,
						{
							tenantId: arg.tenantId,
						}
					)
				)
				try {
					await cacheDataLoaded
					// @ts-expect-error Check if this is fixed in future https://github.com/aws-amplify/amplify-js/issues/7589
					subscription.subscribe({
						next: ({ provider, value }: any) => {
							const data =
								value?.data?.onUpdatePreparePurchaseByTenantId
							if (!data?.id) return
							updateCachedData((draft) => {
								draft.map((item) => {
									if (item.id === data.id) {
										for (const [
											key,
											value,
										] of Object.entries(data)) {
											if (value === null) continue
											if (
												key === 'ocrStatus' &&
												value === 'Complete' &&
												item.ocrStatus !== 'Complete'
											) {
												makeSuccessToast(
													`Ocr processing for ${item.fileName} done`
												)
											}
											const typedKey =
												key as keyof typeof item
											// @ts-expect-error
											item[typedKey] = value as any
										}
									}
									return item
								})
							})
						},
						error: (error: any) => console.warn(error),
					})
				} catch (error) {
					console.error(error)
				}
				// cacheEntryRemoved will resolve when the cache subscription is no longer active
				await cacheEntryRemoved
				// perform cleanup steps once the `cacheEntryRemoved` promise resolves
				try {
					// @ts-expect-error
					subscription.unsubscribe()
				} catch (err: any) {
					console.warn('err on unsubscribe: ', err)
				}
			},
			transformResponse: (response: ReduxPreparePurchase[]) => response,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'PreparePurchase' as const,
								id: id as string,
							})),
							{ type: 'PreparePurchase', id: 'LIST' },
						]
					: [{ type: 'PreparePurchase', id: 'LIST' }],
		}),
		createPreparePurchaseById: builder.mutation({
			queryFn: async (
				{
					tenantId,
					preparePurchaseData,
					fileUploadDetails,
					modifier,
					userName,
				}: {
					tenantId: string
					preparePurchaseData: Partial<ReduxPreparePurchase>
					fileUploadDetails: ReduxS3Object
					modifier?: 'startOcr'
					userName?: string
				},
				{ dispatch }
			) => {
				try {
					// We save the form value along with the s3 details
					console.log('preparePurchaseData: ', preparePurchaseData)

					let updateQuoteNumber = ''

					if (
						preparePurchaseData.netAmountDetails &&
						preparePurchaseData.netAmountDetails.length > 0
					) {
						const allNetAmtDetails =
							preparePurchaseData.netAmountDetails
						for (const eachNetAmtDetails of allNetAmtDetails) {
							if (eachNetAmtDetails.costlineId) {
								const {
									data: { getRecapInfo: costline },
								} = (await API.graphql(
									graphqlOperation(queries.getRecapInfo, {
										id: eachNetAmtDetails.costlineId,
									})
								)) as {
									data: { getRecapInfo: ReduxRecapInfo }
								}
								const {
									data: { getQuote: quoteDetails },
								} = (await API.graphql(
									graphqlOperation(queries.getQuote, {
										id: costline?.quoteId,
									})
								)) as { data: { getQuote: ReduxQuote } }
								updateQuoteNumber += `${quoteDetails?.quoteNumber},`
								eachNetAmtDetails.costlineName =
									costline.item as any

								eachNetAmtDetails.costlineName_fr =
									costline.item_fr as any
							}
						}
					}
					console.log('preparePurchaseData: ', preparePurchaseData)
					const newPreparePurchase = {
						...preparePurchaseData,
						invoiceDocuments: [fileUploadDetails],
						id: nanoid(),
						tenantId,
						activeFlag: true,

						// costlineId: preparePurchaseData.costlineId,
						// costlineName: newCostlineName,
						// costlineName_fr: newCostlineName_fr,
						quoteNumber: updateQuoteNumber.slice(
							0,
							updateQuoteNumber.length - 1
						),
					}
					console.log('newPreparePurchase: ', newPreparePurchase)
					await API.graphql(
						graphqlOperation(mutations.createPreparePurchase, {
							input: newPreparePurchase,
						})
					)
					if (modifier === 'startOcr') {
						// We check from the modifier if ocr is needed or not
						const newFilename = newPreparePurchase.fileName ?? ''
						dispatch(
							startPreparePurchaseOcr({
								preparePurchaseId: newPreparePurchase.id,
								fileUploadDetails,
								fileName: newFilename,
							})
						)
						const logInput = {
							id: nanoid(),
							activeFlag: true,
							tenantId: tenantId,
							name: userName,
							exportDate: dayjs().toISOString(),
							exportType: 'Started OCR',
							loggedLineId: newPreparePurchase?.id,
						}
						const {
							data: { createExportInfo },
						} = (await API.graphql(
							graphqlOperation(mutations.createExportInfo, {
								input: logInput,
							})
						)) as {
							data: { createExportInfo: ReduxExportInfo }
						}
					}
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
			invalidatesTags: () => [{ type: 'PreparePurchase', id: 'LIST' }],
		}),
		updatePreparePurchaseById: builder.mutation({
			queryFn: async ({
				preparePurchaseId,
				tenantId,
				updatePreparePurchaseInput,
			}: {
				preparePurchaseId: string
				tenantId: string
				updatePreparePurchaseInput: Partial<ReduxPreparePurchase>
			}) => {
				try {
					const {
						data: { getPreparePurchase },
					} = (await API.graphql(
						graphqlOperation(queries.getPreparePurchase, {
							id: preparePurchaseId,
						})
					)) as {
						data: GetPreparePurchaseQueryApi
					}
					if (!getPreparePurchase) {
						return {
							error: `preparePurchaseId: ${preparePurchaseId} does not exist or is null. getPreparePurchase: ${getPreparePurchase}`,
						}
					}

					const updateInput =
						getProperUpdateValues<ReduxPreparePurchase>({
							updatedInput: updatePreparePurchaseInput,
							originalInput: getPreparePurchase,
						})

					await API.graphql(
						graphqlOperation(mutations.updatePreparePurchase, {
							input: updateInput,
						})
					)
					return { data: null }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},

			invalidatesTags: (result, error, { preparePurchaseId }) => [
				{ type: 'PreparePurchase', id: preparePurchaseId },
			],
		}),
	}),
	overrideExisting: false,
})

export const {
	useFetchPreparePurchaseQuery,
	useFetchPreparePurchaseFreelancerQuery,
	useCreatePreparePurchaseByIdMutation,
} = preparePurchaseApi
